/**
 * Unified validation composable that provides consistent validation rules
 * for both vee-validate and Vuetify form validation approaches
 */
export const useValidation = () => {
  // Helper function to parse phone numbers (assuming this exists in your utils)
  const parsePhone = (value: string) => {
    // Simplified phone validation - adjust based on your existing parsePhone function
    const cleaned = value.replace(/\D/g, '')
    if (cleaned.length < 10 || cleaned.length > 15) {
      throw new Error('Invalid phone number')
    }
  }

  // Helper function to validate ID numbers (assuming this exists in your utils)
  const validateIdNumber = (value: string) => {
    if (!value) return false
    const cleaned = value.replace(/\s/g, '')
    // South African ID validation - adjust based on your existing logic
    return cleaned.length === 13 && /^\d{13}$/.test(cleaned)
  }

  // Helper function to get province from postal code (assuming this exists in your utils)
  const getProvinceFromPostalCode = (code: number) => {
    // Simplified validation - adjust based on your existing logic
    return code >= 1000 && code <= 9999
  }

  // Core validation rules that work with both vee-validate and Vuetify
  const rules = {
    // Required field validation
    required: (value: unknown) => !!value || 'This field is required',
    
    // Email validation (optional - allows empty)
    email: (value: string) => {
      if (!value) return true // Allow empty
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return pattern.test(value) || 'Invalid email address'
    },
    
    // Email validation (required)
    emailRequired: (value: string) => {
      if (!value) return 'Email is required'
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return pattern.test(value) || 'Invalid email address'
    },
    
    // Minimum length validation (required)
    minLength: (min: number) => (value: string) => {
      if (!value) return 'This field is required'
      return value.length >= min || `Minimum ${min} characters required`
    },
    
    // Minimum length validation (optional - allows empty)
    minLengthOptional: (min: number) => (value: string) => {
      if (!value) return true // Allow empty
      return value.length >= min || `Minimum ${min} characters required`
    },
    
    // Maximum length validation
    maxLength: (max: number) => (value: string) => {
      if (!value) return true
      return value.length <= max || `Maximum ${max} characters allowed`
    },
    
    // Phone number validation
    phone: (value: string) => {
      if (!value) return true // Allow empty
      try {
        parsePhone(value)
        return true
      } catch {
        return 'Invalid phone number format'
      }
    },
    
    // Phone number validation (required)
    phoneRequired: (value: string) => {
      if (!value) return 'Phone number is required'
      try {
        parsePhone(value)
        return true
      } catch {
        return 'Invalid phone number format'
      }
    },
    
    // South African ID number validation
    idNumber: (value: string) => {
      if (!value) return true // Allow empty
      return validateIdNumber(value) || 'Invalid ID number format'
    },
    
    // South African ID number validation (required)
    idNumberRequired: (value: string) => {
      if (!value) return 'ID number is required'
      return validateIdNumber(value) || 'Invalid ID number format'
    },
    
    // Postal code validation
    postalCode: (value: string) => {
      if (!value) return true // Allow empty
      const paddedCode = value.padStart(4, '0')
      if (!/^\d{4}$/.test(paddedCode)) return 'Invalid postal code format'
      const code = parseInt(paddedCode, 10)
      return getProvinceFromPostalCode(code) || 'Invalid postal code'
    },
    
    // Numeric validation
    numeric: (value: string) => {
      if (!value) return true
      return /^\d+$/.test(value) || 'Must be a number'
    },
    
    // Numeric validation (required)
    numericRequired: (value: string) => {
      if (!value) return 'This field is required'
      return /^\d+$/.test(value) || 'Must be a number'
    },
    
    // URL validation
    url: (value: string) => {
      if (!value) return true
      try {
        new URL(value)
        return true
      } catch {
        return 'Invalid URL format'
      }
    },
    
    // Password confirmation validation
    // Note: This needs to be created dynamically with the password ref
    passwordMatch: (password: Ref<string>) => (value: string) => {
      if (!password.value && !value) return true
      return value === password.value || 'Passwords do not match'
    },
  }

  // Vee-validate schema generators for common patterns
  const schemas = {
    // Login form schema
    login: {
      id: rules.required,
      password: rules.required,
    },
    
    // User account schema (for registration/update)
    userAccount: {
      firstname: rules.required,
      lastname: rules.required,
      email: rules.emailRequired,
      phone: rules.phoneRequired,
      idNumber: rules.idNumberRequired,
      password: rules.minLength(8),
    },
    
    // Basic contact info schema
    contact: {
      name: rules.required,
      email: rules.email,
      phone: rules.phone,
      message: rules.required,
    },
  }

  // Helper to create password confirmation rules
  const createPasswordConfirmation = (passwordRef: Ref<string>) => ({
    password: rules.minLength(8),
    confirmPassword: rules.passwordMatch(passwordRef),
  })

  return {
    rules,
    schemas,
    createPasswordConfirmation,
  }
}